import * as React from "react";
import { useStore,log } from "../index";
import { IClausionStateStore } from "../../zustand/store";
import { useShallow } from "zustand/shallow";
import {
  Card,
  CardHeader,
  Body1,
  Switch,
  Button,
  Dropdown,
  Option,
  Link,
  useToastController,
  useId,
  Toaster,
  Toast,
  ToastIntent,
  ToastTitle,
  OptionOnSelectData,
  makeStyles,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Spinner,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  SplitButton,
} from "@fluentui/react-components";
import { CalculatorArrowClockwiseRegular, ArrowClockwiseRegular, Info20Regular, ArrowSyncRegular } from "@fluentui/react-icons";
import { refreshModeOptions } from "../../zustand/types";
import { useTranslation } from "react-i18next";
import RefreshCustomFunctions from "./util/RefreshCustomFunctions";
import globalStyles from "./CommonStyle";
import { useAxios } from "../providers/AxiosProvider";

const cacheTtlOptions = [
  { value: "0", label: "cache_ttl_disabled" },
  { value: "5", label: "cache_ttl_5_min" },
  { value: "15", label: "cache_ttl_15_min" },
  { value: "30", label: "cache_ttl_30_min" },
  { value: "60", label: "cache_ttl_1_hour" },
  { value: "180", label: "cache_ttl_3_hours" },
  { value: "360", label: "cache_ttl_6_hours" },
  { value: "720", label: "cache_ttl_12_hours" },
  { value: "1440", label: "cache_ttl_1_day" },
];

// Define styles using makeStyles
const useStyles = makeStyles({
  root: {
    borderRadius: "0em !important",
    boxShadow: "none !important",
    borderTop: "1px solid #dee1ea",
    marginBottom: "12px",
    paddingBottom: "0px",
  },
  cacheRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    paddingRight: "12px",
    marginBottom: "8px",
  },
  cacheDropdown: {
    minWidth: "120px",
  },
  splitButton: {
    width: "100%",
    "& > button:first-child": {
      flexGrow: 1,
    },
    "& button": {
      backgroundColor: "#278838",
      color: "#fff",
      fontWeight: "normal",
      ":hover": {
        backgroundColor: "#1f6d2d",
        color: "#fff",
      },
      ":active": {
        backgroundColor: "#1e7e34",
        color: "#fff",
      },
    },
  },
});
const CalculationCard: React.FunctionComponent = () => {
  const globalStyle = globalStyles();
  const { t } = useTranslation();
  const classes = useStyles();

  const automaticCalculation = useStore(useShallow((state: IClausionStateStore) => state.automaticCalculation));
  const [setAutomaticCalculation] = useStore(
    useShallow((state: IClausionStateStore) => [state.setAutomaticCalculation])
  );
  const refreshMode = useStore(useShallow((state: IClausionStateStore) => state.refreshMode));
  const [setRefreshMode] = useStore(useShallow((state: IClausionStateStore) => [state.setRefreshMode]));
  const isRefreshInProgress = useStore(useShallow((state: IClausionStateStore) => state.isRefreshInProgress));
  const cacheTtl = useStore(useShallow((state: IClausionStateStore) => state.cacheTtl));
  const [setCacheTtl] = useStore(useShallow((state: IClausionStateStore) => [state.setCacheTtl]));
  const [setForceRefreshTimestamp] = useStore(useShallow((state: IClausionStateStore) => [state.setForceRefreshTimestamp]));

  const [isTooltipVisible, setIsTooltipVisible] = React.useState(false); // State for tooltip visibility
  const buttonRef = React.useRef<HTMLButtonElement>(null); // Ref for the button

  const { axiosInstance } = useAxios();

  // Handle clicks outside the button to close the tooltip
  React.useEffect(() => {
    // Listen for clicks outside the button to close the tooltip
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsTooltipVisible(false);
      }
    };

    if (isTooltipVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isTooltipVisible]);

  // Sync calculation mode from Excel
  React.useEffect(() => {
    Excel.run(async (context) => {
      const app = context.workbook.application;
      app.load("calculationMode");
      await context.sync();
      setAutomaticCalculation(app.calculationMode === Excel.CalculationMode.automatic);
    });
  }, []);

  const onRefreshModeSelected = (event: React.SyntheticEvent, data: OptionOnSelectData) => {
    console.log(event, data);
    setRefreshMode(data.optionValue);
    log.info(`Refresh Mode: ${data.optionValue}`);
  };

  const onCacheTtlSelected = (_event: React.SyntheticEvent, data: OptionOnSelectData) => {
    const value = Number(data.optionValue);
    setCacheTtl(value);
    log.info(`Cache TTL: ${value} minutes`);
  };

  async function forceRefreshCustomFunctions() {
    log.info("Force Refresh button clicked");
    try {
      setForceRefreshTimestamp(Date.now());
      await axiosInstance.get("/api/auth/user");
      await RefreshCustomFunctions();
      notify("success");
    } catch (error) {
      notify("error");
    }
  }

  const onAutomaticCalculationChange = async () => {
    await Excel.run(async (context) => {
      const app = context.workbook.application;
      // Toggle calculation mode based on current value
      app.calculationMode = automaticCalculation
        ? Excel.CalculationMode.manual
        : Excel.CalculationMode.automatic;
      await context.sync();
      app.load("calculationMode");
      await context.sync();
      setAutomaticCalculation(app.calculationMode === Excel.CalculationMode.automatic);
      log.info(`Automatic Calculation: ${app.calculationMode}`);
    });
  };

  async function refreshCustomFunctions() {
    log.info("Refresh button clicked");
    try {
      await axiosInstance.get("/api/auth/user");
      await RefreshCustomFunctions();
      notify("success");
    } catch (error) {
      notify("error");
    }
  }

  const toasterId = useId("toaster");
  const { dispatchToast } = useToastController(toasterId);
  const notify = (intent: ToastIntent) => {
    let notifyText: string;
    switch (intent) {
      case "success":
        notifyText = t("calculation_toast_successful");
        break;
      case "error":
        notifyText = t("calculation_toast_error");
        break;
      default:
        notifyText = t("calculation_toast_unknown");
        break;
    }

    dispatchToast(
      <Toast>
        <ToastTitle>{notifyText}</ToastTitle>
      </Toast>,
      { intent: intent }
    );
  };

  return (
    <Card className={classes.root}>
      <CardHeader
        header={
          <Body1 className={globalStyle.headerContainer}>
            {/* Group the icon and title together */}
            <span className={globalStyle.titleContainer}>
              <CalculatorArrowClockwiseRegular />
              <span className={`${globalStyle.fontStyle} ${globalStyle.font16}`}>{t("calculation_title")}</span>
            </span>
            <Popover>
              <PopoverTrigger disableButtonEnhancement>
                <Button
                  ref={buttonRef} // Attach ref to the button
                  appearance="transparent"
                  icon={<Info20Regular />}
                  size="small" // Reduce button size
                  onClick={() => setIsTooltipVisible(!isTooltipVisible)} // Toggle tooltip visibility
                />
              </PopoverTrigger>

              <PopoverSurface className={globalStyle.popoverSurface}>
                <div>
                  <p>
                    {t("calculation_text")}{" "}
                  </p>
                </div>
              </PopoverSurface>
            </Popover>
          </Body1>
        }
      />
      <div className={globalStyle.switchContainer}>
        <span className={globalStyle.label}>
          {automaticCalculation ? t("calculation_switch_on") : t("calculation_switch_off")}
        </span>
        <Switch checked={automaticCalculation} onChange={onAutomaticCalculationChange} />
      </div>
      <span className={globalStyle.font14}>{t("calculation_refresh_mode")}</span>
      <Dropdown
        onOptionSelect={(event, data) => onRefreshModeSelected(event, data)}
        selectedOptions={[refreshMode]}
        value={t(`calculation_option_${refreshMode.toLowerCase()}`)}
      >
        {refreshModeOptions.map((option) => (
          <Option key={option} value={option}>{t(`calculation_option_${option.toLowerCase()}`)}</Option>
        ))}
      </Dropdown>
      <div className={classes.cacheRow}>
        <span className={globalStyle.font14}>{t("cache_ttl_label")}</span>
        <Dropdown
          className={classes.cacheDropdown}
          value={t(cacheTtlOptions.find((o) => o.value === String(cacheTtl))?.label || "cache_ttl_5_min")}
          selectedOptions={[String(cacheTtl)]}
          onOptionSelect={onCacheTtlSelected}
        >
          {cacheTtlOptions.map((option) => (
            <Option key={option.value} value={option.value}>
              {t(option.label)}
            </Option>
          ))}
        </Dropdown>
      </div>
      <Menu positioning="below-end">
        <MenuTrigger disableButtonEnhancement>
          {(triggerProps) => (
            <SplitButton
              className={classes.splitButton}
              menuButton={triggerProps}
              primaryActionButton={{
                onClick: () => refreshCustomFunctions(),
                disabled: isRefreshInProgress,
              }}
              icon={isRefreshInProgress ? <Spinner size="tiny" /> : <ArrowClockwiseRegular />}
              disabled={isRefreshInProgress}
            >
              {isRefreshInProgress ? t("calculation_refresh_in_progress") : t("calculation_refresh_button")}
            </SplitButton>
          )}
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem
              icon={<ArrowSyncRegular />}
              onClick={() => forceRefreshCustomFunctions()}
              disabled={isRefreshInProgress}
            >
              {t("cache_force_refresh_button")}
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
      <Toaster toasterId={toasterId} />
    </Card>
  );
};

export default CalculationCard;
